import dayjs from 'dayjs'
import { CalendarIcon } from 'lucide-react'
import { type ReactNode, forwardRef, useState } from 'react'
import { type DayPickerProps } from 'react-day-picker'

import { Calendar } from '@/components/calendar/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/popover/popover'

import { cn } from '@/lib/utils'

import { Button } from '../button/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select/select'
import { MONTH_OPTIONS, YEAR_OPTIONS } from './constants'

export interface DatePickerProps {
  value: Date | undefined
  formatString?: string
  placeholder?: ReactNode
  disabled?: boolean
  disabledDateRule?: DayPickerProps['disabled']
  onSelect?: (selectedDate?: Date | '') => void
}

export const DatePicker = forwardRef<HTMLButtonElement, DatePickerProps>(
  ({ value, formatString, placeholder, disabled, disabledDateRule, onSelect }, ref) => {
    const [year, setYear] = useState<string | undefined>(dayjs(value).format('YYYY'))
    const [monthDate, setMonthDate] = useState<Date | undefined>(dayjs(value ?? new Date()).toDate())

    const handleChangeYear = (year: string) => {
      const newDate = dayjs(value).year(+year).toDate()
      setYear(year)
      setMonthDate(newDate)
    }

    const handleChangeMonth = (month: string) => {
      const newDate = dayjs(monthDate).month(+month).toDate()
      setMonthDate(newDate)
    }

    const handleSelectDate = (selectedDate: Date | undefined) => {
      onSelect?.(selectedDate ?? '')
    }

    return (
      <Popover>
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            ref={ref}
            variant={'outline'}
            className={cn(
              'w-full justify-start text-left font-normal',
              'disabled:bg-secondary disabled:text-slate-500 disabled:opacity-100',
              !value && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? dayjs(value).format(formatString ?? 'MM-DD-YYYY') : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <ul className={cn('grid grid-cols-2 gap-3', 'w-full', 'px-3 pt-3')}>
            <Select value={year} onValueChange={handleChangeYear}>
              <SelectTrigger>
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {YEAR_OPTIONS.map(({ label, value }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={`${dayjs(monthDate).month()}`} onValueChange={handleChangeMonth}>
              <SelectTrigger>
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {MONTH_OPTIONS.map(({ label, value }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </ul>
          <Calendar
            mode="single"
            selected={value}
            month={monthDate}
            disabled={disabledDateRule}
            onSelect={handleSelectDate}
          />
        </PopoverContent>
      </Popover>
    )
  }
)
DatePicker.displayName = 'DatePicker'
